import React, { useState, useEffect } from "react";
import CanvasJSReact from "@canvasjs/react-charts";
import { VscOpenPreview } from "react-icons/vsc";
import { FaEdit, FaExpandAlt } from "react-icons/fa";
import DrawingOverlay from "./DrawingOverlay";
import {
  CircularProgressbarWithChildren,
  buildStyles,
} from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

function QualityDashboard({
  expanded,
  setExpanded,
  selectedYear,
  selectedQuarter,
}) {
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const headerStyle = {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 10,
  };
  const [passingRateFullData, setPassingRateFullData] = useState({
    current: [],
    previous: [],
  });
  const [passingRateLevel, setPassingRateLevel] = useState({
    currentlevel: [],
    previouslevel: [],
  });
  const [qualitypassingRateFullData, setQualityPassingRateFullData] = useState({
    current: [],
    previous: [],
  });
  const [highestPassing, setHighestPassing] = useState(null);
  const [lowestPassing, setLowestPassing] = useState(null);
  const [highestQualityPassing, setHighestQualityPassing] = useState(null);
  const [lowestQualityPassing, setLowestQualityPassing] = useState(null);
  const [qualityTrendData, setQualityTrendData] = useState({
    current: [],
    previous: [],
  });
  const [overallQualityRate, setOverallQualityRate] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPassingRate = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API}/getPassingRateByCourse?qtr=${selectedQuarter}&year=${selectedYear}`
        );

        const { success, current = [], previous = [] } = res.data;

        if (success) {
          const courseMap = {};

          current.forEach((item) => {
            const code = item.course_code;
            courseMap[code] = {
              label: code || "Unknown",
              course_level: item.course_level || "Unknown",
              current: parseFloat(item.totalRate) || 0,
              previous: 0,
            };
          });

          previous.forEach((item) => {
            const code = item.course_code;
            if (!courseMap[code]) {
              courseMap[code] = {
                label: code || "Unknown",
                course_level: item.course_level || "Unknown",
                current: 0,
                previous: parseFloat(item.totalRate) || 0,
              };
            } else {
              courseMap[code].previous = parseFloat(item.totalRate) || 0;
            }
          });

          const mergedData = Object.values(courseMap).sort((a, b) =>
            (a.course_level || "").localeCompare(b.course_level || "")
          );

          const fullCurrent = mergedData.map((d) => ({
            label: d.label,
            y: d.current,
            course_level: d.course_level,
          }));

          const fullPrevious = mergedData.map((d) => ({
            label: d.label,
            y: d.previous,
            course_level: d.course_level,
          }));

          if (fullCurrent.length > 0) {
            const sorted = [...fullCurrent].sort((a, b) => b.y - a.y);
            setHighestPassing(sorted[0]);
            setLowestPassing(sorted[sorted.length - 1]);
          }

          setPassingRateFullData({
            current: fullCurrent,
            previous: fullPrevious,
          });
        }
      } catch (err) {
        console.error("Failed to fetch passing rate", err);
      }
    };

    const fetchPassingRateLevel = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API}/getPassingRateByLevel?qtr=${selectedQuarter}&year=${selectedYear}`
        );
        const { success, current = [], previous = [] } = res.data;

        if (success) {
          const fullCurrentLevel = current.map((item) => ({
            label: item.course_level?.trim() || "Unknown",
            y: parseFloat(item.totalRate) || 0,
          }));

          const fullPreviousLevel = previous.map((item) => ({
            label: item.course_level?.trim() || "Unknown",
            y: parseFloat(item.totalRate) || 0,
          }));

          setPassingRateLevel({
            currentlevel: fullCurrentLevel,
            previouslevel: fullPreviousLevel,
          });

          console.log("Current:", fullCurrentLevel);
          console.log("Previous:", fullPreviousLevel);
        }
      } catch (err) {
        console.error("Failed to fetch passing rate by level", err);
      }
    };

    const fetchQualityRate = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API}/getQualityByCourse?qtr=${selectedQuarter}&year=${selectedYear}`
        );

        const { success, current = [], previous = [] } = res.data;

        if (success) {
          const courseMap = {};
          current.forEach((item) => {
            const code = item.course_code;
            courseMap[code] = {
              label: code || "Unknown",
              current: parseFloat(item.totalRate) || 0,
              previous: 0,
            };
          });

          previous.forEach((item) => {
            const code = item.course_code;
            if (!courseMap[code]) {
              courseMap[code] = {
                label: code || "Unknown",
                current: 0,
                previous: parseFloat(item.totalRate) || 0,
              };
            } else {
              courseMap[code].previous = parseFloat(item.totalRate) || 0;
            }
          });

          const fullCurrent = Object.values(courseMap).map((d) => ({
            label: d.label,
            y: d.current,
          }));

          const fullPrevious = Object.values(courseMap).map((d) => ({
            label: d.label,
            y: d.previous,
          }));

          if (fullCurrent.length > 0) {
            const sorted = [...fullCurrent].sort((a, b) => b.y - a.y);
            setHighestQualityPassing(sorted[0]);
            setLowestQualityPassing(sorted[sorted.length - 1]);
          }

          setQualityPassingRateFullData({
            current: fullCurrent,
            previous: fullPrevious,
          });

          // ✅ Calculate overall quality rate here
          const total = fullCurrent.reduce((sum, item) => sum + item.y, 0);
          const avg = fullCurrent.length > 0 ? total / fullCurrent.length : 0;
          setOverallQualityRate(avg);
        }
      } catch (err) {
        console.error("Failed to fetch quality rate", err);
      }
    };

    const fetchQualityByCourseLevelTrend = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API}/getQualityByCourseLevel?qtr=${selectedQuarter}&year=${selectedYear}`
        );

        const { success, current = [], previous = [] } = res.data;

        if (success) {
          const formattedCurrent = current.map((item) => ({
            label: item.level_name?.trim() || "Unknown",
            y: parseFloat(item["ROUND(AVG(r.totalRate),2)"]) || 0,
          }));

          const formattedPrevious = previous.map((item) => ({
            label: item.level_name?.trim() || "Unknown",
            y: parseFloat(item["ROUND(AVG(r.totalRate),2)"]) || 0,
          }));

          setQualityTrendData({
            current: formattedCurrent,
            previous: formattedPrevious,
          });
        }
      } catch (err) {
        console.error("Failed to fetch quality by course level trend", err);
      }
    };

    fetchPassingRate();
    fetchPassingRateLevel();
    fetchQualityRate();
    fetchQualityByCourseLevelTrend();
  }, [selectedQuarter, selectedYear]);

  const handleToggleExpand = () => {
    setExpanded(!expanded);
  };

  const handleViewTable = () => {
    const currentQuarter = `Q${selectedQuarter} ${selectedYear}`;
    const previousQuarter =
      selectedQuarter === 1
        ? `Q4 ${selectedYear - 1}`
        : `Q${selectedQuarter - 1} ${selectedYear}`;

    navigate("/detailpassingrate", {
      state: {
        selectedQuarters: [currentQuarter, previousQuarter],
        currentData: passingRateFullData.current,
        previousData: passingRateFullData.previous,
      },
    });
  };

  const handleQualityTable = () => {
    const currentQuarter = `Q${selectedQuarter} ${selectedYear}`;
    const previousQuarter =
      selectedQuarter === 1
        ? `Q4 ${selectedYear - 1}`
        : `Q${selectedQuarter - 1} ${selectedYear}`;

    navigate("/detailqualityofpasses", {
      state: {
        selectedQuarters: [currentQuarter, previousQuarter],
        currentData: qualitypassingRateFullData.current,
        previousData: qualitypassingRateFullData.previous,
      },
    });
  };

  const chartBoxStyle = {
    backgroundColor: "#2f2f2f",
    borderRadius: 10,
    padding: 20,
    minWidth: 300,
    height: "300px",
    position: "relative",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    boxShadow:
      "inset 0 2px 6px rgba(0, 0, 0, 0.5), inset 0 -2px 4px rgba(255, 255, 255, 0.05)",
  };

  const iconStyle = {
    position: "absolute",
    top: 8,
    right: 15,
    color: "#bbb",
    cursor: "pointer",
  };

  const highLowBox = {
    backgroundColor: "#2C2C2C",
    borderRadius: 10,
    padding: 20,
    // flex: 1,
    boxShadow:
      "inset 0 2px 6px rgba(0, 0, 0, 0.5), inset 0 -2px 4px rgba(255, 255, 255, 0.05)",
  };

  return (
    <div
      style={{
        padding: "20px",
        backgroundColor: "#373636",
        margin: 20,
        borderRadius: 10,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <h5 style={{ color: "#fff", margin: 0 }}>Passing Rate & Quality of Passes</h5>

        <div style={{ display: "flex", gap: 12 }}>
          <FaEdit
            size={20}
            style={{ color: "#fff", cursor: "pointer" }}
            onClick={() => setIsDrawingMode(!isDrawingMode)}
          />
          <FaExpandAlt
            size={20}
            onClick={handleToggleExpand}
            style={{ color: "#fff", cursor: "pointer" }}
            title={expanded ? "Collapse" : "Expand"}
          />
        </div>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: "20px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 20,
            flex: 1,
            minWidth: 250,
            height: "400px",
          }}
        >
          <div style={{ ...highLowBox, flex: 1 }}>
            <div style={{ color: "#aaa", fontSize: 14, marginBottom: 20 }}>
              Passing Rate (High - Low)
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
              }}
            >
              {highestPassing && lowestPassing ? (
                <>
                  <div style={{ textAlign: "center" }}>
                    <div
                      style={{
                        fontSize: 25,
                        color: "#4CAF50",
                        fontWeight: "bold",
                      }}
                    >
                      {highestPassing.y.toFixed(2)}%
                    </div>
                    <div style={{ fontSize: 12, color: "#ccc" }}>
                      {highestPassing.label}
                    </div>
                  </div>

                  <div style={{ textAlign: "center" }}>
                    <div
                      style={{
                        fontSize: 25,
                        color: "#F44336",
                        fontWeight: "bold",
                      }}
                    >
                      {lowestPassing.y.toFixed(2)}%
                    </div>
                    <div style={{ fontSize: 12, color: "#ccc" }}>
                      {lowestPassing.label}
                    </div>
                  </div>
                </>
              ) : (
                <div style={{ color: "#ccc", fontSize: 14 }}>Loading...</div>
              )}
            </div>
          </div>

          <div style={{ ...highLowBox, flex: 1, marginTop: "8px" }}>
            <div style={{ color: "#aaa", fontSize: 14, marginBottom: 20 }}>
              Quality of Passes (High - Low)
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
              }}
            >
              {highestQualityPassing && lowestQualityPassing ? (
                <>
                  <div style={{ textAlign: "center" }}>
                    <div
                      style={{
                        fontSize: 25,
                        color: "#4CAF50",
                        fontWeight: "bold",
                      }}
                    >
                      {highestQualityPassing.y.toFixed(2)}%
                    </div>
                    <div style={{ fontSize: 12, color: "#ccc" }}>
                      {highestQualityPassing.label}
                    </div>
                  </div>

                  <div style={{ textAlign: "center" }}>
                    <div
                      style={{
                        fontSize: 25,
                        color: "#F44336",
                        fontWeight: "bold",
                      }}
                    >
                      {lowestQualityPassing.y.toFixed(2)}%
                    </div>
                    <div style={{ fontSize: 12, color: "#ccc" }}>
                      {lowestQualityPassing.label}
                    </div>
                  </div>
                </>
              ) : (
                <div style={{ color: "#ccc", fontSize: 14 }}>Loading...</div>
              )}
            </div>
          </div>

          <div
            style={{
              backgroundColor: "#2f2f2f",
              minHeight: "300px",
              padding: "20px",
              borderRadius: "10px",
              color: "#fff",
              display: "flex",
              flexDirection: "column",
              gap: "15px",
              justifyContent: "center",
              alignItems: "center",
              boxShadow:
                "inset 0 2px 6px rgba(0, 0, 0, 0.5), inset 0 -2px 4px rgba(255, 255, 255, 0.05)",
            }}
          >
            <div style={{ color: "#aaa", fontSize: 14, marginBottom: 10 }}>
              Quality of Passes
            </div>

            <div style={{ width: 180, height: 180 }}>
              <CircularProgressbarWithChildren
                value={overallQualityRate}
                maxValue={100}
                styles={buildStyles({
                  pathColor: "#4CAF50",
                  trailColor: "#999",
                  textColor: "#fff",
                })}
              >
                <div
                  style={{ fontSize: 24, fontWeight: "bold", marginTop: -10 }}
                >
                  {overallQualityRate.toFixed(2)}%
                </div>
                <div style={{ fontSize: 14, color: "#ccc", marginTop: "10px" }}>
                  KPI - 90%
                </div>
              </CircularProgressbarWithChildren>
            </div>

            <div style={{ fontSize: 14, color: "#ccc" }}>
              Overall Quality of Passes - {overallQualityRate.toFixed(2)}%
            </div>
          </div>
        </div>

        {/* Column 2: Bar Charts */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 20,
            flex: 2,
            minWidth: 350,
          }}
        >
          <div style={chartBoxStyle}>
            <div style={iconStyle}>
              <div style={{ ...iconStyle, display: "flex", gap: 10 }}>
                <div title="View Passing Rate Table">
                  <VscOpenPreview size={18} onClick={handleViewTable} />
                </div>
              </div>
            </div>

            <div style={headerStyle}>PASSING RATE (Course Code)</div>
            <CanvasJSChart
              options={{
                height: 220,
                animationEnabled: true,
                backgroundColor: "#2f2f2f",
                axisX: {
                  labelFontColor: "#ccc",
                  title: "Courses",
                  interval: 1,
                  titleFontColor: "#ccc",
                },
                axisY: {
                  labelFontColor: "#ccc",
                  suffix: "%",
                  title: "Passing Rate",
                  titleFontColor: "#ccc",
                  maximum: 100,
                },
                toolTip: {
                  shared: true,
                },
                legend: {
                  cursor: "pointer",
                  fontColor: "#fff",
                },
                data: [
                  {
                    type: "column",
                    name: `Q${selectedQuarter || 1} ${selectedYear}`,
                    showInLegend: true,
                    color: "#5AB267",
                    dataPoints: passingRateFullData.current,
                  },
                  {
                    type: "column",
                    name: `Q${
                      selectedQuarter === 1 ? 4 : selectedQuarter - 1
                    } ${
                      selectedQuarter === 1 ? selectedYear - 1 : selectedYear
                    }`,
                    showInLegend: true,
                    color: "#AEE6B6",
                    dataPoints: passingRateFullData.previous,
                  },
                ],
              }}
            />
          </div>

          <div style={chartBoxStyle}>
            <div style={iconStyle}>
              <div style={{ ...iconStyle, display: "flex", gap: 10 }}>
                <div title="View Quality Passes Rate Table">
                  <VscOpenPreview size={18} onClick={handleQualityTable} />
                </div>
              </div>
            </div>
            <div style={headerStyle}>QUALITY OF PASSES</div>
            <CanvasJSChart
              options={{
                height: 220,
                animationEnabled: true,
                backgroundColor: "#2f2f2f",
                axisX: {
                  labelFontColor: "#ccc",
                  title: "Courses",
                  titleFontColor: "#ccc",
                  interval: 1,
                },
                axisY: {
                  labelFontColor: "#ccc",
                  suffix: "%",
                  title: "Quality Rate",
                  titleFontColor: "#ccc",
                  maximum: 100,
                },
                toolTip: {
                  shared: true,
                },
                legend: {
                  cursor: "pointer",
                  fontColor: "#fff",
                },
                data: [
                  {
                    type: "column",
                    name: `Q${selectedQuarter || 1} ${selectedYear}`,
                    showInLegend: true,
                    color: "#E63A57",
                    dataPoints: qualitypassingRateFullData.current,
                  },
                  {
                    type: "column",
                    name: `Q${
                      selectedQuarter === 1 ? 4 : selectedQuarter - 1
                    } ${
                      selectedQuarter === 1 ? selectedYear - 1 : selectedYear
                    }`,
                    showInLegend: true,
                    color: "#F292A2",
                    dataPoints: qualitypassingRateFullData.previous,
                  },
                ],
              }}
            />
          </div>
        </div>

        {/* Column 3: Doughnut + Line Charts */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 20,
            flex: 2,
            minWidth: 350,
          }}
        >
          <div style={chartBoxStyle}>
            <div style={headerStyle}>PASSING RATE (Course Level)</div>
            <CanvasJSChart
              options={{
                height: 220,
                animationEnabled: true,
                backgroundColor: "#2f2f2f",
                axisX: {
                  labelFontColor: "#ccc",
                  title: "Course Level",
                  interval: 1,
                  titleFontColor: "#ccc",
                },
                axisY: {
                  labelFontColor: "#ccc",
                  suffix: "%",
                  title: "Passing Rate",
                  titleFontColor: "#ccc",
                  maximum: 100,
                },
                toolTip: {
                  shared: true,
                },
                legend: {
                  cursor: "pointer",
                  fontColor: "#fff",
                },
                data: [
                  {
                    type: "column",
                    name: `Q${selectedQuarter || 1} ${selectedYear}`,
                    showInLegend: true,
                    color: "#FFA500",
                    dataPoints: passingRateLevel.currentlevel,
                  },
                  {
                    type: "column",
                    name: `Q${
                      selectedQuarter === 1 ? 4 : selectedQuarter - 1
                    } ${
                      selectedQuarter === 1 ? selectedYear - 1 : selectedYear
                    }`,
                    showInLegend: true,
                    color: "#F2D092",
                    dataPoints: passingRateLevel.previouslevel,
                  },
                ],
              }}
            />
          </div>

          <div style={chartBoxStyle}>
            <div style={headerStyle}>QUALITY OF PASSES TREND</div>
            <CanvasJSChart
              options={{
                height: 220,
                animationEnabled: true,
                backgroundColor: "#2f2f2f",
                axisY: {
                  suffix: "%",
                  labelFontColor: "#ccc",
                  title: "Passing Rate",
                  titleFontColor: "#ccc",
                },
                axisX: {
                  labelFontColor: "#ccc",
                  title: "Courses",
                  titleFontColor: "#ccc",
                },
                toolTip: {
                  shared: true,
                },
                legend: {
                  cursor: "pointer",
                  fontColor: "#fff",
                },
                data: [
                  {
                    type: "line",
                    name: `Q${selectedQuarter} ${selectedYear}`,
                    showInLegend: true,
                    color: "#E63A57",
                    indexLabel: "{y}%",
                    indexLabelFontColor: "#fff",
                    dataPoints: qualityTrendData.current,
                  },
                  {
                    type: "line",
                    name: `Q${
                      selectedQuarter === 1 ? 4 : selectedQuarter - 1
                    } ${
                      selectedQuarter === 1 ? selectedYear - 1 : selectedYear
                    }`,
                    showInLegend: true,
                    color: "#5AB267",
                    indexLabel: "{y}%",
                    indexLabelFontColor: "#fff",
                    dataPoints: qualityTrendData.previous,
                  },
                ],
              }}
            />
          </div>
        </div>
      </div>
      {isDrawingMode && (
        <DrawingOverlay onClose={() => setIsDrawingMode(false)} />
      )}
    </div>
  );
}

export default QualityDashboard;
